import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, distinct, map, mergeMap, switchMap } from 'rxjs/operators';
import * as actions from './actions';
import { AutoBlockSetting } from './model';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';

interface BatchedItem<T = AutoBlockSetting> {
    results: T[];
}

@Injectable()
export class UserAutoBlockEffects {
    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadUsersAutoBlock),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    map((data) => {
                        return actions.loadUsersAutoBlockSuccess({ _tenant, data });
                    }),
                    catchError((error) => of(actions.loadUsersAutoBlockFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    enableUsersLockout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.enableUsersAutoBlock),
            concatMap(({ _tenant, users }) =>
                this.chunkedUserRequest(_tenant, users, `/api/octiga/v3/tenant/account-lockout/users`).pipe(
                    map(() =>
                        actions.enableUsersAutoBlockSuccess({
                            _tenant,
                            users: users.map((user) => ({
                                ...user,
                                autoblockEnabled: true,
                                lastUpdated: new Date().toISOString(),
                            })),
                        }),
                    ),
                    catchError((error) => of(actions.enableUsersAutoBlockFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    disableUsersLockout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.disableUsersAutoBlock),
            concatMap(({ _tenant, users }) =>
                this.chunkedUserRequest<BatchedItem<AutoBlockSetting>>(
                    _tenant,
                    users,
                    `/api/octiga/v3/tenant/account-lockout/users/disable`,
                ).pipe(
                    map((_) => {
                        return actions.disableUsersAutoBlockSuccess({
                            _tenant,
                            users: users.map((user) => ({
                                ...user,
                                autoblockEnabled: false,
                                lastUpdated: new Date().toISOString(),
                            })),
                        });
                    }),
                    catchError((error) => of(actions.disableUsersAutoBlockFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    updateLastModifiedUserAutoBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateLastModifiedUserAutoBlock),
            concatMap(({ _tenant, data }) =>
                this.ajax
                    .put<AutoBlockSetting>(_tenant, `/api/octiga/v3/tenant/account-lockout/user/last-updated`, {
                        userId: data.userId,
                        upn: data.upn,
                    })
                    .pipe(
                        map((user) => actions.updateLastModifiedUserAutoBlockSuccess({ _tenant, user })),
                        catchError((error) => of(actions.updateLastModifiedUserAutoBlockFailure({ _tenant, error }))),
                    ),
            ),
        ),
    );

    private chunkedUserRequest<T>(_tenant: string, users: any[], url: string): Observable<BatchedItem<T>[]> {
        const chunkSize = 12;
        const chunks: (typeof users)[] = [];
        for (let i = 0; i < users.length; i += chunkSize) {
            chunks.push(users.slice(i, i + chunkSize));
        }

        const requests$ = chunks.map((chunk) => this.ajax.put<BatchedItem<T>>(_tenant, url, { users: chunk }));

        return forkJoin(requests$);
    }

    private get(tenant: string) {
        return this.ajax.get(tenant, `api/octiga/v3/tenant/account-lockout/users`);
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
    ) {}
}
